import { Component, HostListener, OnInit, ChangeDetectorRef } from '@angular/core';
import { ManageProfileService } from '../manage-profile.service';
import { LocalService } from '@app/@core/services/local.service';
import { HttpService } from '@core/services/http.service';
import { forkJoin, Subject } from 'rxjs';
import { UpdateContactComponent } from '../update-contact/update-contact.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { patternMatch } from '@app/shared/constants/validators.model';
import { take, takeUntil } from 'rxjs';
import { CommonService } from '@app/shared/services/common.service';
import { AuthService } from '@app/@core/services/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import { modalButtonTitles, modalContent, modalHeading } from '@app/shared/constants/modal.model';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'app-manage-profile-form',
  templateUrl: './manage-profile-form.component.html',
  styleUrls: ['./manage-profile-form.component.scss']
})
export class ManageProfileFormComponent implements OnInit {

  profile = {
        firstName:'',
        middleName:'',
        lastName:'',
        suffix:'',
        dob:'',
        itin:'',
        ssn:'',
        email:'',
        recovery_email:'',
        phoneNumber:'',
        gender:'',
        race:'',
        ethinicity:'',
    alternate_phone: ''
    // add other fields as needed
  }
  showIdentity: boolean = true;
  showContact: boolean = true;
  showDemographic: boolean = true;
  genderList: any[] = [];
  ethinicityList: any[] = [];
  raceList: any[] = [];
  genericMessages: any[] = [];
  suffixList: any[] = [];
  placeholderMessages: any[] = [];
  errorMessages: any[] = [];
  toastMessages: any[] = [];
  errorMessage: any = {};
  toastMessage: any = {}; // Used for toast message lookup, not for display
  genericMessage: any = {};
  placeholderMessage: any = {};
  loadComplete: boolean = false;
  showSSN: boolean = true;
  showITIN: boolean = true;
  ssnFocused: boolean = false;
  SSNDetected: boolean = false;
  ITINDetected: boolean = false;
  idTypeDetected: string = '';
  displayedSSN = '';
  displayedITIN = '';
  SSNError: boolean = false;
  showSSNTooltip: boolean = false;
  showITINTooltip: boolean = false;

  // Toast properties for custom toast
  showToast: boolean = false;
  toastType: string = '';
  toastMessageText: string = '';
  toastTitle: string = '';
  toastTimer: any;
  saveText: string = 'Update';
  person_id: number = 0; // Initialize person_id
  recoveryEmailTooltip: boolean = false;
  emailTooltip: boolean = false;
  copyOfOriginalValue: any = {};
  suffixValue: any = '';
  dobValue: any = '';
  formatPhoneNumber: any = '';
  viewport: string = 'large';
  updatedFlag: boolean = false;
  isLegacyUser = false
  receivedSSN = null;
  manageProfileForm!: FormGroup;
  isSSNFocused = false;
  isITINFocused = false;
  ssnExists: boolean = false;
  itinExists: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(private readonly manageProfileService: ManageProfileService,
    private localstorage: LocalService,
    private fb: FormBuilder,
    private httpService: HttpService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private commonService: CommonService,
    private readonly router: Router,
    private authService: AuthService) {
  }

  @HostListener('window:resize', ['$event'])
  /**
   * Handles window resize events and updates viewport size.
   * @param event The resize event.
   */
  onResize(event: any) {
    this.viewPortSize();
  }
  private formatSSN(value: string): string {
    const digits = value.replace(/\D/g, '').substring(0, 9);
    if (digits.length >= 6) return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
    if (digits.length >= 4) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return digits;
  }

  /**
   * Initialize method to load the person data and populate data in the html template
   */
  ngOnInit(): void {
    this.viewPortSize();
    this.loadComplete = true;

    this.isLegacyUser = this.commonService.getLegacyFlag();
    this.loadDropdownData();

    // Initialize form with SSN validator only (no required)
    this.manageProfileForm = this.fb.group({
      ssn: ['', [this.validateSSN]],
      itin: ['', []],
      receivedSSN: [null, Validators.required]
    });


    // ✅ Dynamic SSN required logic based on radio button
    this.manageProfileForm.get('receivedSSN')?.valueChanges.subscribe(value => {
      const ssnControl =this.ssnControl;
      if (value === true) {
        ssnControl?.setValidators([
          Validators.required,
          this.validateSSN
        ]);
      } else {
          ssnControl?.clearValidators();
        }
      ssnControl?.updateValueAndValidity();
    });

    // ✅ Update displayed SSN/ITIN and reset server-side errors
   this.ssnControl?.valueChanges.subscribe(() => {
      this.ssnExists = false;
    });

   this.itinControl?.valueChanges.subscribe(() => {
      this.itinExists = false;
    });

    // ✅ Enable Save button only when actual changes occur
    this.manageProfileForm.valueChanges.subscribe(() => {
      this.updatedFlag = this.hasFormChanged();
    });
  }
  validateSSN(control: any) {
    const value = control.value || '';
    const digits = value.replace(/\D/g, '');

    // Must be exactly 9 digits long
    if (!digits || digits.length !== 9) {
      return { pattern: true };
    }

    // No part may be all zeros
    if (
      digits.substring(0, 3) === '000' ||
      digits.substring(3, 5) === '00' ||
      digits.substring(5, 9) === '0000'
    ) {
      return { pattern: true };
    }

    // No repeated digits
    if (/^(\d)\1{8}$/.test(digits)) {
      return { pattern: true };
    }

    // SSNs cannot begin with 900-999
    const firstThreeDigits = parseInt(digits.substring(0, 3));
    if (firstThreeDigits >= 900 && firstThreeDigits <= 999) {
      return { pattern: true };
    }

    // Cannot start with 666
    if (digits.substring(0, 3) === '666') {
      return { pattern: true };
    }

    // Invalid SSNs for media
    if (
      digits.substring(0, 6) === '987654' &&
      parseInt(digits.substring(6)) >= 320 &&
      parseInt(digits.substring(6)) <= 329
    ) {
      return { pattern: true };
    }

    // Specific invalid SSN
    if (digits === '078051120') {
      return { pattern: true };
    }

    return null;
  }
  hasFormChanged(): boolean {
    if (!this.copyOfOriginalValue || !this.copyOfOriginalValue.person_id) {
      return false;
    }
    
    const currentValues = this.manageProfileForm.getRawValue();
    const ssnChanged = currentValues.receivedSSN === true
      ? currentValues.ssn !== this.formatSSN(this.profile.ssn || '')
      : false;
    const itinChanged = currentValues.itin !== (this.profile.itin || '');
    const receivedSSNChanged = currentValues.receivedSSN !== (this.profile.ssn ? true : false);
    const normalizeValue = (val: any) => (val === '' || val === null || val === undefined) ? null : val;
    
    // Demographic fields change detection
    const genderChanged = normalizeValue(this.profile.gender) !== normalizeValue(this.copyOfOriginalValue?.cd_gender);
    const raceChanged = normalizeValue(this.profile.race) !== normalizeValue(this.copyOfOriginalValue?.cd_race);
    const ethnicityChanged = normalizeValue(this.profile.ethinicity) !== normalizeValue(this.copyOfOriginalValue?.cd_ethnicity);
    
    // Contact information fields change detection
    const emailChanged = this.normalizeEmailValue(this.profile.email) !== this.normalizeEmailValue(this.copyOfOriginalValue?.primary_email);
    const phoneChanged = this.normalizePhoneValue(this.profile.phoneNumber) !== this.normalizePhoneValue(this.copyOfOriginalValue?.primary_phone);
    const recoveryEmailChanged = this.normalizeEmailValue(this.profile.recovery_email) !== this.normalizeEmailValue(this.copyOfOriginalValue?.recovery_email);
    const altPhoneChanged = this.normalizePhoneValue(this.profile.alternate_phone) !== this.normalizePhoneValue(this.copyOfOriginalValue?.alternate_phone);
    
    // Debug logging
    console.log('Change Detection:', {
      phoneChanged,
      currentPhone: this.profile.phoneNumber,
      originalPhone: this.copyOfOriginalValue?.primary_phone,
      genderChanged,
      raceChanged,
      ethnicityChanged,
      emailChanged,
      recoveryEmailChanged,
      altPhoneChanged
    });
    
    return ssnChanged || itinChanged || receivedSSNChanged || genderChanged || raceChanged || ethnicityChanged ||
           emailChanged || phoneChanged || recoveryEmailChanged || altPhoneChanged;
  }

  private normalizePhoneValue(value: any): string | null {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    return value.toString().replace(/\D/g, '');
  }

  private normalizeEmailValue(value: any): string | null {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    return value.toString().trim().toLowerCase();
  }

  /**
   * Handles maskBlur event from ssnMask directive
   * Note: API check is handled by onSSNInput/onITINInput when 9 digits are entered
   */
  onMaskBlur(event: { type: "SSN" | "ITIN"; value: string }): void {
    // maskBlur event exists for the directive's masking functionality
    // We don't need to call checkSSNOrITIN here since it's already called on input
  }

  /**
   * Handles input event for SSN field - checks for duplicate immediately when 9 digits are entered
   */
  onSSNInput(event: any): void {
    const value = event.target.value.replace(/\D/g, '');
    
    if (value.length === 9) {
      // Check immediately when 9 digits are entered
      this.checkSSNOrITIN('SSN');
    }
  }

  /**
   * Handles input event for ITIN field - checks for duplicate immediately when 9 digits are entered
   */
  onITINInput(event: any): void {
    const value = event.target.value.replace(/\D/g, '');
    
    if (value.length === 9) {
      // Check immediately when 9 digits are entered
      this.checkSSNOrITIN('ITIN');
    }
  }

  checkSSNOrITIN(type: 'SSN' | 'ITIN'): void {
    const ssnValue =this.ssnControl?.value.replace(/\D/g, '');
    const itinValue =this.itinControl?.value.replace(/\D/g, '');

    const request: any = { idType: type };
    if (type === 'SSN') {
      request.ssn = ssnValue;
    } else {
      request.itin = itinValue;
    }
    
    this.manageProfileService.checkSSN(request)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data.exists) {
            this.SSNError = true;
            this.ssnExists = true;
            const msg = this.toastMessage['T0001'];
            this.showFailureToastMsg(msg);

            setTimeout(() => {
              this.SSNError = false;
            }, 5000);
          } else {
            this.SSNError = false;
            this.ssnExists = false;
            this.itinExists = false;
            // No duplicate check here anymore
          }
        },
        error: (err) => {
          console.error('Error checking SSN/ITIN:', err);
        }
      });
      }

  // Helper function to mask email
 private maskEmail(email: string): string {
  if (!email || email.length < 8) return email;

  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;

  // Mask local part: first 2 characters + ****
  const maskedLocal =
    localPart.length > 2
      ? localPart.substring(0, 2) + '****'
      : localPart;

  // Split domain into name and TLD
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return email;

  // Mask domain name: first 2 characters + ***
  const maskedDomain =
    domainParts[0].length > 2
      ? domainParts[0].substring(0, 2) + '***'
      : domainParts[0];

  // Preserve TLD(s)
  const tld = domainParts.slice(1).join('.');

  return `${maskedLocal}@${maskedDomain}.${tld}`;
}

  // Helper function to mask phone
  private maskPhone(phone: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return '*'.repeat(digits.length);
    // Format: 9**-9**-9999
    return `${digits[0]}**-${digits[3]}**-${digits.slice(6)}`;
  }

  // Helper function to mask SSN
  private maskSSN(ssn: string): string {
    const digits = ssn ? ssn.replace(/\D/g, '') : '';
    if (digits.length !== 9) return '*'.repeat(digits.length);
    return `***-**-${digits.slice(-4)}`;
  }

  private openDuplicateAccountModal(): void {
    const maskedName = `${this.profile.firstName} ${this.profile.lastName}`;
    const maskedEmail = this.maskEmail(this.profile.email);
    const maskedPhone = this.maskPhone(this.profile.phoneNumber);
    const maskedSSN = this.maskSSN(this.ssnControl?.value);

    const modalRef = this.modalService.open(ModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true
    });

    modalRef.componentInstance.modalConfig = {
      heading: modalHeading.accountMayExist,
      modalContent: modalContent.accountMayExist(maskedName, maskedEmail, maskedPhone, maskedSSN),
      hasTemplate: true,
      buttons: [
        { text: modalButtonTitles.returnToForm, code: 'N', btnOutline: true },
        { text: modalButtonTitles.logOutAndForgotEmail, code: 'Y' }
      ],
      align: false,
      hideCloseIcon: false
    };

    modalRef.componentInstance.emitData.subscribe((result: any) => {
      if (result?.confirmed) {
        this.authService.logout();
        this.router.navigate(['app-login/forgot-email']);
      } else {
        this.updatedFlag = false;
      }
    });
  }

  @HostListener('document:click', ['$event']) onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target.closest('.tooltip-container')) {
      this.showSSNTooltip = false;
      this.emailTooltip = false;
      this.recoveryEmailTooltip = false;
      this.showITINTooltip = false;
    }
  }

  openSSNTooltip(): void {
    if (window.innerWidth > 768) {
      this.showSSNTooltip = true;
    }
  }

  closeSSNTooltip(): void {
    if (window.innerWidth > 768) {
      this.showSSNTooltip = false;
    }
  }

  toggleSSNTooltip(event?: Event): void {
    event?.stopPropagation();
    this.showSSNTooltip = !this.showSSNTooltip;
  }

  openITINooltip(): void {
    if (window.innerWidth > 768) {
      this.showITINTooltip = true;
    }
  }

  closeITINTooltip(): void {
    if (window.innerWidth > 768) {
      this.showITINTooltip = false;
    }
  }

  toggleITINooltip(event?: Event): void {
    event?.stopPropagation();
    this.showITINTooltip = !this.showITINTooltip;
  }

  openEmailooltip(): void {
    if (window.innerWidth > 768) {
      this.emailTooltip = true;
    }
  }

  closeEmailTooltip(): void {
    if (window.innerWidth > 768) {
      this.emailTooltip = false;
    }
  }

  toggleEmailTooltip(event?: Event): void {
    event?.stopPropagation();
    this.emailTooltip = !this.emailTooltip;
  }

  openRecoveryEmailooltip(): void {
    if (window.innerWidth > 768) {
      this.recoveryEmailTooltip = true;
    }
  }

  closeRecoveryEmailTooltip(): void {
    if (window.innerWidth > 768) {
      this.recoveryEmailTooltip = false;
    }
  }

  toggleRecoveryEmailTooltip(event?: Event): void {
    event?.stopPropagation();
    this.recoveryEmailTooltip = !this.recoveryEmailTooltip;
  }

  /**
   * Determines the viewport size and sets the viewport property accordingly.
   */
  viewPortSize(){
    const width = window.innerWidth;

    // Example using Bootstrap's breakpoints
    const mdBreakpoint = 768; // For a medium viewport
    const lgBreakpoint = 992; // For a large viewport

    if (width >= lgBreakpoint) {
      this.viewport =  'large';
    } else if (width >= mdBreakpoint) {
      this.viewport =  'medium';
    } else {
      this.viewport =  'small';
    }
  }


  /**
   * This method is used to load the drop down values
  */
  private async loadDropdownData() {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=GENDER_TYPES'),
      this.httpService.get('/domain/lookup?requestType=ETHICITY_TYPES'),
      this.httpService.get('/domain/lookup?requestType=RACE_CODES'),
      this.httpService.get('/domain/lookup?requestType=SUFFIX'),
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES'),
    ]).subscribe({
      next: (item: any) => {
        this.genderList = item[0].data.sort((a, b) => a.value.localeCompare(b.value));
        this.ethinicityList = item[1].data.sort((a, b) => a.value.localeCompare(b.value));

        // For race list, sort alphabetically but move "Prefer Not to Answer" to the end
        this.raceList = item[2].data.sort((a, b) => a.value.localeCompare(b.value));

        const elementToMoveIndex = this.raceList.findIndex(item => item.key === '6');
        console.log(elementToMoveIndex);
        if (elementToMoveIndex !== -1) {
          const [elementToMove] = this.raceList.splice(elementToMoveIndex, 1);
          console.log(elementToMove);
          this.raceList.push(elementToMove);
        }

        this.suffixList = item[3].data;
        const noneIndex = this.suffixList.findIndex(item => item.value === 'None');
        if (noneIndex !== -1) {
          const noneItem = this.suffixList.splice(noneIndex, 1)[0];
          this.suffixList.push(noneItem);
        }


        this.errorMessages = item[4].data;
        this.toastMessages = item[5].data;
        this.genericMessages = item[6].data;
        this.placeholderMessages = item[7].data;
        this.errorMessage = {};
        this.errorMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.errorMessage[message.key] = message.value;
          }
        });
        this.localstorage.setItem('errorMessage', JSON.stringify(this.errorMessage));
        this.toastMessage = {};
        this.toastMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.toastMessage[message.key] = message.value;
          }
        });
        this.localstorage.setItem('toastMessage', JSON.stringify(this.toastMessage));
        console.log(item);
        this.genericMessage = {};
        this.genericMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.genericMessage[message.key] = message.value;
          }
        });
        this.localstorage.setItem('genericMessage', JSON.stringify(this.genericMessage));
        this.placeholderMessage = {};
        this.placeholderMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.placeholderMessage[message.key] = message.value;
          }
        });
        this.localstorage.setItem('placeholderMessage', JSON.stringify(this.placeholderMessage));
        this.loadProfileData();
      },
      error: error => {
        console.error('Error loading dropdown data:', error);
        this.loadProfileData();

      }
    });


  }

  /**
   * Loads the profile data for the current user and updates the profile object and related UI state.
   */
  loadProfileData(){
    const user_id = this.localstorage.getItem('id');
    this.manageProfileService.getApplicantDetails(user_id).subscribe(response => {
      //this.profile = response?.data;
      this.profile.firstName = response?.data?.first_name || '';
      this.profile.middleName = response?.data?.middle_name || '';
      this.profile.lastName = response?.data?.last_name || '';
      this.profile.suffix = response?.data?.cd_suffix || '';
      this.profile.dob = response?.data?.date_of_birth || '';
      this.profile.itin = response?.data?.itin_number || '';
      this.profile.ssn = response?.data?.ssn_number || '';
      this.profile.email = response?.data?.primary_email || '';
      this.profile.recovery_email = response?.data?.recovery_email || '';
      this.profile.phoneNumber = this.formatPhoneForDisplay(response?.data?.primary_phone || '');
      this.profile.gender = response?.data?.cd_gender || '';
      this.profile.race = response?.data?.cd_race || '';
      this.profile.ethinicity = response?.data?.cd_ethnicity || '';
      this.profile.alternate_phone = this.formatPhoneForDisplay(response?.data?.alternate_phone || '');
      this.person_id = response?.data?.person_id;
      //Doing this avoid memory reference issue in typescript
      this.copyOfOriginalValue = JSON.parse(JSON.stringify(response?.data));
      this.formatPhone(this.profile.phoneNumber);
      const formattedSSN = this.formatSSN(this.profile.ssn || '');


      this.manageProfileForm.patchValue({
        ssn: formattedSSN,
        itin: this.profile.itin || '',
        receivedSSN: this.profile.ssn ? true : this.manageProfileForm.get('receivedSSN')?.value
      }, { emitEvent: false });
      
      this.updatedFlag = false;
      setTimeout(() => {
        this.updatedFlag = false;
      }, 100);

      setTimeout(() => {
        const ssnInput = document.querySelector('#inputfield_manage_profile_ssn') as HTMLInputElement;
        if (ssnInput) {
          ssnInput.setAttribute('data-show-ssn', 'false');
          ssnInput.dispatchEvent(new Event('resetMask'));
        }
        
        const itinInput = document.querySelector('#inputfield_manage_profile_itin') as HTMLInputElement;
        if (itinInput) {
          itinInput.setAttribute('data-show-ssn', 'false');
          itinInput.dispatchEvent(new Event('resetMask'));
        }
      }, 50);

      if (this.profile.ssn) {
        this.ssnControl?.disable();
      }
      // ✅ If SSN exists and ITIN does NOT exist, make SSN required
      if (this.profile.ssn && !this.profile.itin) {
        const ssnControl =this.ssnControl;
        ssnControl?.setValidators([
          Validators.required,
          Validators.pattern(patternMatch.formattedSSN),
          this.validateSSN
        ]);
        ssnControl?.updateValueAndValidity();
      }


      if (this.profile.itin) {
       this.itinControl?.disable();
      } else {
        this.manageProfileForm.get('receivedSSN')?.clearValidators();
        this.manageProfileForm.get('receivedSSN')?.updateValueAndValidity();
      }

      // Check SSN independently
      if (this.profile.ssn) {
        this.SSNDetected = true;
        this.idTypeDetected = 'SSN';
        this.manageProfileForm.get('receivedSSN')?.clearValidators();
        this.manageProfileForm.get('receivedSSN')?.updateValueAndValidity();
      } else {
        this.SSNDetected = false;
      }

      // Check ITIN independently
      if (this.profile.itin) {
        const firstDigit = this.profile.itin.charAt(0);
        if (firstDigit === '9') {
          this.ITINDetected = true;
          if (!this.profile.ssn) {
            this.idTypeDetected = 'ITIN';
          }
        } else {
          this.ITINDetected = false;
        }
        this.itinControl?.disable();
      } else {
        this.ITINDetected = false;
      }

      this.suffixList.forEach((element: any)=>{
        if(this.profile.suffix === element.key){
          this.suffixValue = element.value;
        }
      });
      const dobString = this.profile.dob.split("T")[0]; // "2023-01-08"
      const [year, month, day] = dobString.split("-");
      this.dobValue = `${month}/${day}/${year}`;
      console.log('DOB Value:', this.dobValue);

      this.cdr.detectChanges(); // Ensure view updates

      // Reset visibility states to default (hidden) after loading data
      if (this.SSNDetected) {
        this.showSSN = false;
      } else {
        this.showITIN = false;
      }
    });


  }

  /**
   * This method is used to toggle the panel sections
   * @param section 
   * @param direction 
   */
  togglePanel(section: string, direction: string) {
    if (direction === 'up') {
      (section === 'identity') ? this.showIdentity = false : null;
      (section === 'contact') ? this.showContact = false : null;
      (section === 'demographic') ? this.showDemographic = false : null;
    } else {
      (section === 'identity') ? this.showIdentity = true : null;
      (section === 'contact') ? this.showContact = true : null;
      (section === 'demographic') ? this.showDemographic = true : null;
    }
  }

  // ✅ Getter for SSN
  get ssnControl(): AbstractControl | null {
    return this.manageProfileForm.get('ssn');
  }

  // ✅ Getter for ITIN
  get itinControl(): AbstractControl | null {
    return this.manageProfileForm.get('itin');
  }

  // ✅ Getter for receivedSSN
  get receivedSSNControl(): AbstractControl | null {
    return this.manageProfileForm.get('receivedSSN');
  }
  
get isSaveDisabled(): boolean {
  // If no changes, always disable
  if (!this.updatedFlag) return true;

  // If form is invalid, always disable
  if (this.manageProfileForm.invalid) return true;

  // If SSN exists error, always disable
  if (this.ssnExists) return true;

  // Get receivedSSN value
  const receivedSSN = this.manageProfileForm.get('receivedSSN')?.value;
  const ssnControl = this.ssnControl;

  // If receivedSSN is true, require SSN to be filled and valid
  if (receivedSSN === true) {
    if (!ssnControl?.value || ssnControl.invalid) {
      return true;
    }
  }
  if(!this.profile.gender || !this.profile.race || !this.profile.ethinicity ){
        return true
  }
  if(!this.profile.email || this.profile.email.trim() === ''){
        return true
  }
    if(!this.profile.phoneNumber || this.profile.phoneNumber.trim() === ''){
        return true
  }
  return false;
}


  /**
   * Resets the profile's gender, race, ethnicity, and contact information fields to their original values.
   */
  resetProfile() {
    this.updatedFlag = false;
    // Reset demographic fields
    this.profile.gender = this.copyOfOriginalValue?.cd_gender || '';
    this.profile.race = this.copyOfOriginalValue?.cd_race || '';
    this.profile.ethinicity = this.copyOfOriginalValue?.cd_ethnicity || '';
    // Reset contact information fields (commented because email and phonenumbers will update while during edit itself)
    //this.profile.email = this.copyOfOriginalValue?.primary_email || '';
    //this.profile.phoneNumber = this.formatPhoneForDisplay(this.copyOfOriginalValue?.primary_phone || '');
    //this.profile.recovery_email = this.copyOfOriginalValue?.recovery_email || '';
    //this.profile.alternate_phone = this.formatPhoneForDisplay(this.copyOfOriginalValue?.alternate_phone || '');
  }

  /**
   * Method invoked on Save button click
   */
  onSubmit() {
    const postObj: any = {
      first_name: this.profile.firstName,
      middle_name: this.profile.middleName,
      last_name: this.profile.lastName,
      date_of_birth: this.profile.dob,
      primary_email: this.profile.email,
      recovery_email: this.profile.recovery_email,
      primary_phone: this.profile.phoneNumber,
      cd_gender: this.profile.gender,
      cd_race: this.profile.race,
      cd_ethnicity: this.profile.ethinicity,
      user_id: this.localstorage.getItem('id')
    };
    if (this.profile.itin?.trim()) {
      postObj.itin_number = this.profile.itin.trim();
    }
    // Only add cd_suffix if it has a value
    if (this.profile.suffix) {
      postObj.cd_suffix = this.profile.suffix;
    }
    // Only add ssn_number if receivedSSN flag is true
    if (this.receivedSSNControl?.value === true) {
      postObj.ssn_number = this.ssnControl?.value || '';
    }

    // Check if user is entering a new SSN (and ITIN exists)
    const isNewSSN = this.receivedSSNControl?.value === true &&
      this.ssnControl?.value &&
      this.formatSSN(this.profile.ssn || '') !== this.ssnControl?.value &&
      this.profile.itin;

    if (isNewSSN) {
      // Check for duplicate SSN before update
      const ssnValue = this.ssnControl?.value.replace(/\D/g, '');
      const duplicatePayload = {
        first_name: this.profile.firstName,
        last_name: this.profile.lastName,
        birth_date: this.profile.dob?.split('T')[0],
        ssn_number: ssnValue
      };
      this.manageProfileService.checkPossibleDuplicate(duplicatePayload)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (duplicateResponse: any) => {
            if (duplicateResponse.data && duplicateResponse.data.isDuplicate === true) {
              this.openDuplicateAccountModal();
              return;
            } else {
              this.performProfileUpdate(postObj);
            }
          },
          error: (duplicateError) => {
            console.error('Error checking for duplicate:', duplicateError);
            this.showFailureToastMsg('Error checking for duplicate SSN.');
          }
        });
    } else {
      // Proceed as usual if SSN is not being changed or no ITIN
      this.performProfileUpdate(postObj);
    }
  }

  // Helper to perform the actual update
  private performProfileUpdate(postObj: any) {
    this.manageProfileService.updateUserDetails(this.person_id, postObj).subscribe(response => {
      this.updatedFlag = false;
      const msg = this.toastMessage['T0020'];
      this.showSuccessToastMsg(msg);

      // If legacy user, updating the user.is_legacy_user in sessionStorage
      if (this.isLegacyUser) {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          try {
            const userObj = JSON.parse(userStr);
            userObj.additionalFields.is_legacy_user = false;
            sessionStorage.setItem('user', JSON.stringify(userObj));
          } catch (e) {}
        }
        this.isLegacyUser = false;
      }

      //Call to fetch the profile and set the values again
      this.loadProfileData();
    }, error => {
      //console.error('Error updating user details:', error);
      this.showFailureToastMsg('Error updating user details:'+ error.message);
    });
  }
  /**
* Sends a notification email after updating the phone number.
* @param type The type of update (primary, recovery, etc.).
*/
sendProfileUpdateNotification(type:string) {
    const pEmailId = sessionStorage.getItem('email');

  this.manageProfileService.updateProfileNotification(pEmailId,'demographic',type, this.person_id).subscribe({
      next: response => {
        console.log('API Response:', response); // Debug log

        // Treat any non-error response as success
        // this.showSuccessToastMsg(this.capitalizeFirst(type) + ' phone number updated successfully. A confirmation email has been sent.');
        console.log('profile update notification sent', response);
        //this.dialogRef.close({ response: 'success', type: type, newEmail: this.newEmail, newPhone: this.newPhone });
      },
      error: error => {
        console.error('Error sending phone number update notification:', error);
        this.showFailureToastMsg('Failed to send notification for updated update profile.');
        // this.showToastMessage('Failed to update phone number. Please try again.', 'error');
      }
    });
  }

  /**
   * Opens the edit modal for email or phone number updates and handles the result.
   * @param mailType The type of contact info to edit (primaryEmail, recoveryEmail, phoneNumber).
   */
  openEditModal(mailType){
    let dialogRef: MatDialogRef<any>;
    if(mailType === 'primaryEmail'){
      dialogRef = this.dialog.open(UpdateContactComponent, {
        width: '400px',
        data: { emailType: 'primaryEmail', currentEmail: this.profile.email, user_id: this.person_id, recoveryEmail: this.profile.recovery_email, currentNumber: this.profile.phoneNumber },
        panelClass: 'custom-modalbox'
      });
    } else if(mailType === 'recoveryEmail'){
      dialogRef = this.dialog.open(UpdateContactComponent, {
        width: '400px',
        data: { emailType: 'recoveryEmail', currentEmail: this.profile.recovery_email, user_id: this.person_id, primaryEmail: this.profile.email, currentNumber: this.profile.phoneNumber },
        panelClass: 'custom-modalbox'
      });
    } else if(mailType === 'phoneNumber'){
      dialogRef = this.dialog.open(UpdateContactComponent, {
        width: '400px',
        data: { emailType: 'phoneNumber', currentNumber: this.profile.phoneNumber, user_id: this.person_id, primaryEmail: this.profile.email, recoveryEmail: this.profile.recovery_email },
        panelClass: 'custom-modalbox'
      });
    }
      else if(mailType === 'altPhoneNumber'){
      dialogRef = this.dialog.open(UpdateContactComponent, {
        width: '400px',
        data: { emailType: 'altPhoneNumber', currentNumber: this.profile.alternate_phone, user_id: this.person_id, primaryEmail: this.profile.email, recoveryEmail: this.profile.recovery_email },
        panelClass: 'custom-modalbox'
      });
    }

    dialogRef.afterClosed().subscribe(result => {
      if(result.response === 'success'){
        this.toastTitle = '';
        this.toastMessageText = 'Contact Information Updated.';
        this.toastType = 'success';
        this.showToast = true;
        this.setShortToastTimer();
        this.saveText = 'Update';

        if(result.type === 'primaryEmail'){
          this.profile.email = result.newEmail;
          this.updatedFlag = true;
          setTimeout(() => {
            this.authService.resetLoggedIn().subscribe((res: any) => {
              if (res.data) {
                window.location.href = res.data.logoutURL;
                this.localstorage.clear();
              } else {
                this.authService.logout();
              }
            }, () => {
              this.authService.logout();
            });
          }, 3000);
        } else if(result.type === 'recoveryEmail'){
          this.profile.recovery_email = result.newEmail;
          this.updatedFlag = true;
        } else if(result.type === 'phoneNumber'){
          this.profile.phoneNumber = this.formatPhoneForDisplay(result.newPhone);
          this.formatPhone(result.newPhone);
          this.updatedFlag = true;
        } else if(result.type === 'altPhoneNumber'){
          this.profile.alternate_phone = this.formatPhoneForDisplay(result.newPhone);
          this.updatedFlag = true;
        }
        this.cdr.detectChanges(); // Ensure view updates
      }else{
        //cancel logic
      }
    });
  }
  // Toast timer and close methods
  /**
   * Sets a short timer to hide the toast message after 5 seconds.
   */
  setShortToastTimer() {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.showToast = false;
    }, 4000); // 4 seconds
  }

  /**
   * Closes the current toast message.
   */
  closeToast() {
    this.showToast = false;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  /**
   * Formats a phone number string to +1 (XXX) XXX-XXXX for 11-digit or (XXX) XXX-XXXX for 10-digit format.
   * @param phoneNumber The phone number to format.
   */
  private formatPhoneForDisplay(phone: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
    }
    if (digits.length === 10) {
      return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    return phone;
  }

  /**
   * Formats a phone number string to +1 (XXX) XXX-XXXX for 11-digit or (XXX) XXX-XXXX for 10-digit format.
   * @param phoneNumber The phone number to format.
   */
  formatPhone(phoneNumber: string) {
    if (!phoneNumber) {
      this.formatPhoneNumber = '';
      return;
    }

    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    // Handle 11-digit numbers (with country code)
    if (digits.length === 11 && digits.startsWith('1')) {
      this.formatPhoneNumber = `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
    }
    // Handle 10-digit numbers (without country code)
    else if (digits.length === 10) {
      this.formatPhoneNumber = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    // Fallback for unexpected formats
    else {
      this.formatPhoneNumber = phoneNumber;
    }
  }

  /**
   * Displays a failure toast message with the provided message.
   * @param msg The message to display.
   */
  showFailureToastMsg(msg: string){
    this.toastTitle = '';
    this.toastMessageText = msg;
    this.toastType = 'error';
    this.showToast = true;
    this.setShortToastTimer();
    window.scrollTo(0,0);
  }

  /**
   * Displays a success toast message with the provided message.
   * @param msg The message to display.
   */
    showSuccessToastMsg(msg: string){
    this.toastTitle = '';
    this.toastMessageText = msg;
    this.toastType = 'success';
    this.showToast = true;
    this.setShortToastTimer();
    window.scrollTo(0,0);
  }

  /**
   * Closes the toast message with the given element ID.
   * @param id The element ID of the toast to close.
   */
  closeToastMsg(id: string){
    const element = document.getElementById(id);
    if(element){
      element.parentElement.removeChild(element);
    }
  }

  /**
   * Angular lifecycle hook. Cleans up resources on destroy.
   */
  ngOnDestroy(){
    
    this.destroy$.next();
    this.destroy$.complete();

  }


}